import { Typography } from "@mui/material";
import PropTypes from "prop-types";
import { Fragment, useEffect, useState } from "react";
import {
  ChangePreferredVerificationModal,
  ContainedButtonWithForwardArrow,
  StandAloneCheckbox,
  phoneNumberMasker,
} from "react-common-ui-library";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  CONSUMER_DASHBOARD_ROUTE,
  CONSUMER_USER,
  LEGAL_NOTICE_ROUTE,
  LOGIN_ROUTE,
  WIDTH_100_PERCENT,
  WIDTH_50_PERCENT,
  TEXT,
} from "../../../Constants/CommonConstants";
import { VERIFICATION_METHOD_UPDATED_SUCCESSFULLY } from "../../../Constants/LoginConstants";
import {
  CHANGE_PREFERRED_VERIFICATION,
  CHANGE_PREFERRED_VERIFICATION_BUTTON_ID,
  GO_TO_DASHBOARD,
  GO_TO_DASHBOARD_BUTTON_ID,
  VERIFICATION_METHODS_CHECKBOX_ID,
  VERIFICATION_METHODS_CHECKBOX_LABEL,
  VERIFICATION_METHODS_CHECKBOX_TEXT_ID,
  checkBoxLabel,
} from "../../../Constants/LoginVerificationMethodsConstants";
import {
  SLICE_LOGIN,
  THUNK_PUT_VERIFICATION_METHOD,
} from "../../../Constants/SliceAndThunkConstants";
import {
  clearLoginApiResponse,
  putAccount,
  putVerificationMethod,
} from "../../../Features/LoginSlice";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import { manageAuthorizationSelector } from "../../../Selectors/AuthorizationSelector";
import { manageLoginSelector } from "../../../Selectors/LoginSelector";
import { navigateToFpactPage } from "../../../Services/CommonServices";
import {
  StyledChangePreferredVerificationButton,
  StyledCheckboxWrapper,
  StyledVerificationMethodButtonWrapper,
  StyledVerificationMethodTextButtonWrapper,
} from "../../../Styles/Login/LoginVerificationMethods/LoginVerificationMethodsStyles";
import { getMyProfile } from "../../../Features/MyProfileSlice";

/**
 * @description Login Verification Methods Checkbox component allows the user to show or skip verification methods page on each login.
 * @returns {ReactNode} Renders the check box, go to dashboard button and change preferred verification methods button when applicable.
 */

export const LoginVerificationMethodsCheckbox = ({ isProfileRoute }) => {
  const intl = useIntl();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { phoneNo, emailId, prefVerificationMethod } = useSelector((state) =>
    manageLoginSelector(state)
  );

  const { scopes } = useSelector((state) => manageAuthorizationSelector(state));

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(state, SLICE_LOGIN, THUNK_PUT_VERIFICATION_METHOD)
  );

  const { isSuccess, description } = apiResponses;

  const onGoToDashboardClick = () => {
    scopes === CONSUMER_USER
      ? navigateToFpactPage(CONSUMER_DASHBOARD_ROUTE)
      : navigate(`/${LOGIN_ROUTE}/${LEGAL_NOTICE_ROUTE}`);
  };

  const [openChangeModal, setOpenChangeModal] = useState(false);

  const onCancelClick = () => {
    setOpenChangeModal(false);
  };

  const handleOnSubmit = (data) => {
    if (data.prefVerificationMethod !== prefVerificationMethod) {
      dispatch(putVerificationMethod(data));
    } else {
      setOpenChangeModal(false);
    }
  };

  const handleCheckboxChange = (e) => {
    const payload = { isUserVerificationMethodRemembered: false };
    if (e.target.checked) {
      payload.isUserVerificationMethodRemembered = e.target.checked;
    }
    dispatch(putAccount(payload));
  };

  const onChangeButtonClick = () => {
    setOpenChangeModal(true);
  };

  useEffect(() => {
    if (isSuccess && description === VERIFICATION_METHOD_UPDATED_SUCCESSFULLY) {
      setOpenChangeModal(false);
      dispatch(getMyProfile());
    }
    dispatch(clearLoginApiResponse(THUNK_PUT_VERIFICATION_METHOD));
  }, [description, dispatch, isSuccess]);

  return (
    <>
      {!isProfileRoute ? (
        <Fragment>
          <StyledCheckboxWrapper>
            <StandAloneCheckbox
              {...checkBoxLabel}
              id={VERIFICATION_METHODS_CHECKBOX_ID}
              onChange={handleCheckboxChange}
            />
            <Typography id={VERIFICATION_METHODS_CHECKBOX_TEXT_ID}>
              {intl.formatMessage({
                id: VERIFICATION_METHODS_CHECKBOX_LABEL,
              })}
            </Typography>
          </StyledCheckboxWrapper>
          <StyledVerificationMethodButtonWrapper>
            <ContainedButtonWithForwardArrow
              id={GO_TO_DASHBOARD_BUTTON_ID}
              onClick={onGoToDashboardClick}
            >
              {intl.formatMessage({ id: GO_TO_DASHBOARD })}
            </ContainedButtonWithForwardArrow>
          </StyledVerificationMethodButtonWrapper>
        </Fragment>
      ) : null}
      <StyledVerificationMethodTextButtonWrapper
        width={isProfileRoute ? WIDTH_100_PERCENT : WIDTH_50_PERCENT}
      >
        {phoneNo && emailId ? (
          <StyledChangePreferredVerificationButton
            variant={TEXT}
            id={CHANGE_PREFERRED_VERIFICATION_BUTTON_ID}
            onClick={onChangeButtonClick}
          >
            {intl.formatMessage({ id: CHANGE_PREFERRED_VERIFICATION })}
          </StyledChangePreferredVerificationButton>
        ) : null}
      </StyledVerificationMethodTextButtonWrapper>
      <ChangePreferredVerificationModal
        open={openChangeModal}
        onCancel={onCancelClick}
        selectContact={prefVerificationMethod}
        phone={phoneNumberMasker(phoneNo)}
        email={emailId}
        handleOnSubmit={handleOnSubmit}
        setOpen={setOpenChangeModal}
      />
    </>
  );
};

LoginVerificationMethodsCheckbox.defaultProps = {
  isProfileRoute: false,
};

LoginVerificationMethodsCheckbox.propTypes = {
  isProfileRoute: PropTypes.bool,
};
export default LoginVerificationMethodsCheckbox;
